import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, List, LayoutGrid, Settings, Pin, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { progressAPI, gedAPI } from '@/lib/api';
import UpgradeModal from '@/components/UpgradeModal';

// Import from refactored modules
import { ALL_TOOLS, FREE_TOOL_KEYS, CATEGORY_ORDER } from './constants';
import { settingsAPI } from './api';
import { GridToolCard, ListToolCard } from './ToolCards';
import { CategorySection } from './CategorySection';
import { HiddenToolsSettingsPanel, HiddenToolsBanner } from './HiddenToolsPanel';
import { QuickActionsTips } from './QuickActionsTips';
import { UserAccessBadge, FreeUserUpgradeBanner } from './UpgradeBanner';
import { QuickActionsGridSkeleton } from '@/components/dashboard/DashboardSkeleton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuickActionsGrid = ({
  user,
  userSettings,
  setUserSettings,
  accessStatus: propAccessStatus,  // Accept access status from parent
  progressBadges: propProgressBadges,  // Accept progress badges from parent
  gedStatus: propGedStatus,  // Accept GED status from parent
  gpsData: propGpsData  // Accept GPS data from parent
}) => {
  const navigate = useNavigate();

  // View mode: 'list' (categorized - DEFAULT) or 'grid' (original dashboard)
  const [viewMode, setViewMode] = useState('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});

  // Premium gating state - use prop if available, otherwise fetch
  const [hasPremiumAccess, setHasPremiumAccess] = useState(propAccessStatus?.hasPremiumAccess || propAccessStatus?.has_premium_access || false);
  const [showAvailableOnly, setShowAvailableOnly] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [userAccessStatus, setUserAccessStatus] = useState(propAccessStatus || null);
  
  // Organization premium access state
  const [orgPremiumAccess, setOrgPremiumAccess] = useState({
    hasAccess: propAccessStatus?.orgPremiumAccess?.hasAccess || propAccessStatus?.org_premium_access?.has_access || false,
    fullAccess: propAccessStatus?.orgPremiumAccess?.fullAccess || propAccessStatus?.org_premium_access?.full_access || false,
    tools: propAccessStatus?.orgPremiumAccess?.tools || propAccessStatus?.org_premium_access?.tools || [],
    daysRemaining: propAccessStatus?.orgPremiumAccess?.daysRemaining || propAccessStatus?.org_premium_access?.days_remaining || 0,
    orgName: propAccessStatus?.orgPremiumAccess?.orgName || propAccessStatus?.org_premium_access?.org_name || null
  });

  // Memoize settings values
  const visibility = useMemo(() => userSettings?.dashboard_visibility || {}, [userSettings?.dashboard_visibility]);
  const pinnedTools = useMemo(() => userSettings?.pinned_tools || [], [userSettings?.pinned_tools]);

  // State for showing hidden tools panel
  const [showHiddenTools, setShowHiddenTools] = useState(false);
  const [showToolsSettings, setShowToolsSettings] = useState(false);
  const [toolBadges, setToolBadges] = useState(propProgressBadges || {});
  const [gedApproved, setGedApproved] = useState(propGedStatus?.approved || false);
  const [needsHsEquivalency, setNeedsHsEquivalency] = useState(propGpsData?.needs_hs_equivalency !== false); // Default true to show GED tool

  // Fetch premium access status on mount (skip if provided via props)
  useEffect(() => {
    // If access status is passed as prop, use it
    if (propAccessStatus) {
      setUserAccessStatus(propAccessStatus);
      setHasPremiumAccess(propAccessStatus.hasPremiumAccess || propAccessStatus.has_premium_access);
      if (propAccessStatus.orgPremiumAccess || propAccessStatus.org_premium_access) {
        const orgAccess = propAccessStatus.orgPremiumAccess || propAccessStatus.org_premium_access;
        setOrgPremiumAccess({
          hasAccess: orgAccess?.hasAccess || orgAccess?.has_access || true,
          fullAccess: orgAccess?.fullAccess || orgAccess?.full_access || false,
          tools: orgAccess?.tools || [],
          daysRemaining: orgAccess?.daysRemaining || orgAccess?.days_remaining || 0,
          orgName: orgAccess?.orgName || orgAccess?.org_name || null
        });
      }
      return; // Skip API call
    }
    
    const fetchAccessStatus = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;

        const response = await fetch(`${API_URL}/api/access/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setUserAccessStatus(data);
          setHasPremiumAccess(data.has_premium_access);
          
          // Set org premium access data
          if (data.has_org_premium_access) {
            setOrgPremiumAccess({
              hasAccess: true,
              fullAccess: data.org_premium_full_access || false,
              tools: data.org_premium_tools || [],
              daysRemaining: data.org_premium_days_remaining || 0,
              orgName: data.org_premium_org_name || null
            });
          }
        }
      } catch (error) {
        console.debug('Failed to load access status:', error);
      }
    };
    fetchAccessStatus();
  }, [propAccessStatus]);

  // Fetch tool badge status on mount (skip if provided via props)
  useEffect(() => {
    // Skip if badges provided from parent
    if (propProgressBadges && Object.keys(propProgressBadges).length > 0) {
      setToolBadges(propProgressBadges);
      return;
    }
    
    const fetchBadges = async () => {
      try {
        const response = await progressAPI.getBadgeStatus();
        if (response.data?.badges) {
          setToolBadges(response.data.badges);
        }
      } catch (error) {
        console.debug('Failed to load tool badges:', error);
      }
    };
    fetchBadges();
  }, [propProgressBadges]);

  // Fetch GED approval status on mount (skip if provided via props)
  useEffect(() => {
    // Skip if GED status provided from parent
    if (propGedStatus !== undefined && propGedStatus !== null) {
      setGedApproved(propGedStatus?.approved || false);
      return;
    }
    
    const fetchGedStatus = async () => {
      try {
        // First check if user has ged_user role
        const hasGedUserRole = user?.roles?.includes('ged_user');
        if (hasGedUserRole) {
          setGedApproved(true);
          return;
        }
        
        // Fall back to checking application status
        const response = await gedAPI.getApplicationStatus();
        setGedApproved(response.data?.status === 'approved');
      } catch (error) {
        console.debug('Failed to load GED status:', error);
        // Check user roles as fallback
        setGedApproved(user?.roles?.includes('ged_user') || false);
      }
    };
    fetchGedStatus();
  }, [user?.roles, propGedStatus]);

  // Fetch user's education status for GED tool visibility (skip if provided via props)
  useEffect(() => {
    // Skip if GPS data provided from parent
    if (propGpsData !== undefined && propGpsData !== null) {
      setNeedsHsEquivalency(propGpsData?.needs_hs_equivalency !== false);
      return;
    }
    
    const fetchEducationStatus = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;

        const response = await fetch(`${API_URL}/api/gps/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          // Default to true (show GED tool) if needs_hs_equivalency is not set
          setNeedsHsEquivalency(data.needs_hs_equivalency ?? true);
        }
      } catch (error) {
        console.debug('Failed to load education status:', error);
        setNeedsHsEquivalency(true); // Default to showing GED tool on error
      }
    };
    fetchEducationStatus();

    // Listen for profile updates to refresh education status immediately
    const handleProfileUpdate = () => {
      fetchEducationStatus();
    };
    window.addEventListener('profileUpdated', handleProfileUpdate);
    
    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
  }, [propGpsData]);

  // Super admin check - ONLY super_admin role gets full access
  const isSuperAdmin = user?.roles?.includes('super_admin');

  // Partner check
  const isPartner = user?.roles?.includes('partner') || user?.roles?.includes('organization_partner');

  // Effective premium access (including admin override)
  const effectivePremiumAccess = hasPremiumAccess || isSuperAdmin;
  
  // Check if a specific tool is accessible via org premium access
  const hasOrgToolAccess = useCallback((toolKey) => {
    if (!orgPremiumAccess.hasAccess) return false;
    if (orgPremiumAccess.fullAccess) return true;
    return orgPremiumAccess.tools.includes(toolKey);
  }, [orgPremiumAccess]);
  
  // Check if user can access a specific premium tool (combines all access types)
  const canAccessPremiumTool = useCallback((toolKey) => {
    // Super admins and regular premium users have full access
    if (effectivePremiumAccess) return true;
    // Check org-specific tool access
    return hasOrgToolAccess(toolKey);
  }, [effectivePremiumAccess, hasOrgToolAccess]);

  // Check if tool is visible (not hidden by user)
  const isToolVisible = useCallback((settingsKey) => {
    if (!visibility || Object.keys(visibility).length === 0) return true;
    return visibility[settingsKey] !== false;
  }, [visibility]);

  // Get list of hidden tools
  const hiddenTools = useMemo(() => {
    return ALL_TOOLS.filter(tool => {
      if (tool.superAdminOnly && !isSuperAdmin) return false;
      if (tool.partnerOnly && !isPartner && !isSuperAdmin) return false;
      return visibility[tool.settingsKey] === false;
    });
  }, [visibility, isSuperAdmin, isPartner]);

  // Filter tools based on role and visibility
  const visibleTools = useMemo(() => {
    // Check if user has ged_user role (for GED tool access)
    const hasGedUserRole = user?.roles?.includes('ged_user');
    const hasGedAccess = gedApproved || hasGedUserRole;
    
    // Check if user is on a paid plan (not free)
    const isPaidUser = user?.is_vip || user?.is_founder || user?.is_early_adopter || 
                       user?.is_standard_premium || 
                       ['premium_monthly', 'premium_yearly', 'premium'].includes(user?.subscription_tier);
    
    return ALL_TOOLS.filter(tool => {
      if (tool.superAdminOnly && !isSuperAdmin) return false;
      if (tool.partnerOnly && !isPartner && !isSuperAdmin) return false;
      if (tool.requiresGedApproval && !hasGedAccess && !isSuperAdmin) return false;
      
      // Hide freeUsersOnly tools for paid users (they have premium access already)
      // Exception: Super admins always see it
      if (tool.freeUsersOnly && isPaidUser && !isSuperAdmin) return false;
      
      // Hide GED Learn More tool if user doesn't need HS equivalency
      // Exception: Super admins always see it
      if (tool.key === 'ged_program_learn_more' && !needsHsEquivalency && !isSuperAdmin) return false;
      
      if (!isToolVisible(tool.settingsKey)) return false;
      return true;
    });
  }, [isSuperAdmin, isPartner, isToolVisible, gedApproved, needsHsEquivalency, user?.roles, user?.is_vip, user?.is_founder, user?.is_early_adopter, user?.is_standard_premium, user?.subscription_tier]);

  // Filter for "Show Available Only"
  const accessFilteredTools = useMemo(() => {
    if (!showAvailableOnly || effectivePremiumAccess) return visibleTools;

    return visibleTools.filter(tool => {
      const isFree = FREE_TOOL_KEYS.includes(tool.key);
      const isUserTool = tool.userTool;
      return isFree || !isUserTool;
    });
  }, [visibleTools, showAvailableOnly, effectivePremiumAccess]);

  // Search filtered tools - SORTED: free tools first, then premium
  const searchFilteredTools = useMemo(() => {
    let filtered = accessFilteredTools;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(tool =>
        tool.name.toLowerCase().includes(query) ||
        tool.description.toLowerCase().includes(query)
      );
    }

    // Keep original order - don't re-sort
    return filtered;
  }, [accessFilteredTools, searchQuery]);

  // Get pinned tools
  const pinnedToolsList = useMemo(() => {
    return pinnedTools
      .map(key => accessFilteredTools.find(t => t.key === key))
      .filter(Boolean);
  }, [pinnedTools, accessFilteredTools]);

  // Group tools by category
  const toolsByCategory = useMemo(() => {
    const grouped = {};
    searchFilteredTools.forEach(tool => {
      if (!grouped[tool.category]) grouped[tool.category] = [];
      grouped[tool.category].push(tool);
    });

    // DEFENSIVE SORT: Ensure free tools appear before premium tools within each category
    // This handles any edge cases where constants.js order might not be respected
    Object.keys(grouped).forEach(category => {
      grouped[category].sort((a, b) => {
        const aIsFree = FREE_TOOL_KEYS.includes(a.key);
        const bIsFree = FREE_TOOL_KEYS.includes(b.key);
        // Free tools first (true = 0, false = 1, so free sorts before premium)
        if (aIsFree && !bIsFree) return -1;
        if (!aIsFree && bIsFree) return 1;
        return 0; // Preserve original order among same type
      });
    });

    return grouped;
  }, [searchFilteredTools]);

  // Handle tool click - WITH PREMIUM GATING (supports org-specific tool access)
  const handleToolClick = useCallback((tool, isLocked = false) => {
    const isFree = FREE_TOOL_KEYS.includes(tool.key);
    const isPremiumTool = tool.userTool && !isFree;
    
    // Check if user can access this specific premium tool
    const hasAccess = !isPremiumTool || canAccessPremiumTool(tool.key);
    const shouldBlock = isPremiumTool && !hasAccess;

    if (shouldBlock || isLocked) {
      setSelectedTool(tool);
      setShowUpgradeModal(true);
      return;
    }

    settingsAPI.trackToolUsage(tool.key, tool.name);
    navigate(tool.path);
  }, [navigate, canAccessPremiumTool]);

  // Handle pin toggle
  const handleTogglePin = useCallback(async (toolKey, e) => {
    e.stopPropagation();

    let newPinned;
    if (pinnedTools.includes(toolKey)) {
      newPinned = pinnedTools.filter(k => k !== toolKey);
    } else {
      if (pinnedTools.length >= 10) {
        alert('You can pin up to 10 tools. Please unpin one first.');
        return;
      }
      newPinned = [...pinnedTools, toolKey];
    }

    if (setUserSettings) {
      setUserSettings(prev => ({ ...prev, pinned_tools: newPinned }));
    }

    await settingsAPI.update({ pinned_tools: newPinned });
  }, [pinnedTools, setUserSettings]);

  // Handle hide toggle
  const handleToggleHide = useCallback(async (settingsKey, e) => {
    e.stopPropagation();

    const newVisibility = { ...visibility, [settingsKey]: false };

    if (setUserSettings) {
      setUserSettings(prev => ({ ...prev, dashboard_visibility: newVisibility }));
    }

    await settingsAPI.update({ dashboard_visibility: newVisibility });
  }, [visibility, setUserSettings]);

  // Handle restore single tool
  const handleRestoreTool = useCallback(async (settingsKey) => {
    const newVisibility = { ...visibility };
    delete newVisibility[settingsKey];

    if (setUserSettings) {
      setUserSettings(prev => ({ ...prev, dashboard_visibility: newVisibility }));
    }

    await settingsAPI.update({ dashboard_visibility: newVisibility });
  }, [visibility, setUserSettings]);

  // Handle restore all tools
  const handleRestoreAll = useCallback(async () => {
    if (setUserSettings) {
      setUserSettings(prev => ({ ...prev, dashboard_visibility: {} }));
    }

    await settingsAPI.update({ dashboard_visibility: {} });
    setShowHiddenTools(false);
  }, [setUserSettings]);

  // Toggle category expansion
  const handleToggleCategory = useCallback((categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: prev[categoryKey] === false ? true : (prev[categoryKey] === undefined ? false : !prev[categoryKey])
    }));
  }, []);

  // Render the appropriate tool card based on view mode
  const ToolCard = viewMode === 'grid' ? GridToolCard : ListToolCard;

  return (
    <div className="mb-8 sm:mb-12">
      {/* Quick Action Tips Section */}
      <QuickActionsTips />

      {/* Header with controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-lg sm:text-2xl font-semibold">Quick Actions</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowToolsSettings(!showToolsSettings)}
            className="flex items-center gap-2 border-gray-300 hover:bg-gray-100"
          >
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Dashboard Tools Settings</span>
            <span className="sm:hidden">Settings</span>
          </Button>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          {/* Tool Visibility Filter - Only show for free users */}
          {!effectivePremiumAccess && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600 hidden sm:inline">Show:</span>
              <button
                onClick={() => setShowAvailableOnly(false)}
                className={cn(
                  "px-2 py-1 text-xs rounded transition-colors",
                  !showAvailableOnly ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"
                )}
              >
                All
              </button>
              <button
                onClick={() => setShowAvailableOnly(true)}
                className={cn(
                  "px-2 py-1 text-xs rounded transition-colors",
                  showAvailableOnly ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"
                )}
              >
                Available
              </button>
            </div>
          )}

          {/* Search */}
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search tools..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-white"
            />
          </div>

          {/* View Toggle */}
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                "px-3 py-2 flex items-center gap-1.5 text-sm transition-colors",
                viewMode === 'list'
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              )}
              title="Categorized View"
            >
              <List className="w-4 h-4" />
              <span className="hidden sm:inline">List</span>
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={cn(
                "px-3 py-2 flex items-center gap-1.5 text-sm transition-colors",
                viewMode === 'grid'
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              )}
              title="Dashboard View"
            >
              <LayoutGrid className="w-4 h-4" />
              <span className="hidden sm:inline">Grid</span>
            </button>
          </div>
        </div>
      </div>

      {/* User Access Badge */}
      <UserAccessBadge userAccessStatus={userAccessStatus} />

      {/* Free User Upgrade Banner */}
      <FreeUserUpgradeBanner
        effectivePremiumAccess={effectivePremiumAccess}
        userAccessStatus={userAccessStatus}
      />

      {/* Dashboard Tools Settings Panel */}
      {showToolsSettings && (
        <HiddenToolsSettingsPanel
          hiddenTools={hiddenTools}
          onRestoreTool={handleRestoreTool}
          onRestoreAll={handleRestoreAll}
          onClose={() => setShowToolsSettings(false)}
        />
      )}

      {/* Hidden Tools Banner */}
      <HiddenToolsBanner
        hiddenTools={hiddenTools}
        showHiddenTools={showHiddenTools}
        setShowHiddenTools={setShowHiddenTools}
        onRestoreTool={handleRestoreTool}
        onRestoreAll={handleRestoreAll}
      />

      {/* Pinned Tools Section */}
      {pinnedToolsList.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Pin className="w-5 h-5 text-blue-500" />
            <h3 className="text-lg font-semibold text-gray-900">Pinned Tools</h3>
            <span className="text-xs text-gray-500">({pinnedToolsList.length}/10)</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
            {pinnedToolsList.map(tool => (
              <ToolCard
                key={tool.key}
                tool={tool}
                isPinned={true}
                onToolClick={handleToolClick}
                onTogglePin={handleTogglePin}
                onToggleHide={handleToggleHide}
                badge={toolBadges[tool.key]}
                isPremium={tool.userTool && !FREE_TOOL_KEYS.includes(tool.key)}
                isLocked={tool.userTool && !FREE_TOOL_KEYS.includes(tool.key) && !canAccessPremiumTool(tool.key)}
                hasPremiumAccess={effectivePremiumAccess || hasOrgToolAccess(tool.key)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Main Content */}
      {searchQuery ? (
        // Search Results - Flat Grid
        <div>
          <p className="text-sm text-gray-500 mb-4">
            {searchFilteredTools.length} tools found for &quot;{searchQuery}&quot;
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
            {searchFilteredTools.map(tool => (
              <ToolCard
                key={tool.key}
                tool={tool}
                isPinned={pinnedTools.includes(tool.key)}
                onToolClick={handleToolClick}
                onTogglePin={handleTogglePin}
                onToggleHide={handleToggleHide}
                badge={toolBadges[tool.key]}
                isPremium={tool.userTool && !FREE_TOOL_KEYS.includes(tool.key)}
                isLocked={tool.userTool && !FREE_TOOL_KEYS.includes(tool.key) && !canAccessPremiumTool(tool.key)}
                hasPremiumAccess={effectivePremiumAccess || hasOrgToolAccess(tool.key)}
              />
            ))}
          </div>
        </div>
      ) : viewMode === 'list' ? (
        // Categorized List View (DEFAULT)
        <div>
          {CATEGORY_ORDER.map(categoryKey => (
            <CategorySection
              key={categoryKey}
              categoryKey={categoryKey}
              tools={toolsByCategory[categoryKey] || []}
              pinnedToolKeys={pinnedTools}
              expandedCategories={expandedCategories}
              onToggleCategory={handleToggleCategory}
              onToolClick={handleToolClick}
              onTogglePin={handleTogglePin}
              onToggleHide={handleToggleHide}
              toolBadges={toolBadges}
              hasPremiumAccess={effectivePremiumAccess}
              canAccessPremiumTool={canAccessPremiumTool}
            />
          ))}
        </div>
      ) : (
        // Original Grid View (Dashboard)
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
          {searchFilteredTools
            .filter(t => !pinnedTools.includes(t.key))
            .map(tool => (
              <GridToolCard
                key={tool.key}
                tool={tool}
                isPinned={false}
                onToolClick={handleToolClick}
                onTogglePin={handleTogglePin}
                onToggleHide={handleToggleHide}
                badge={toolBadges[tool.key]}
              />
            ))}
        </div>
      )}

      {/* Empty State */}
      {searchFilteredTools.length === 0 && (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No tools found</h3>
          <p className="text-gray-500 mt-1">Try adjusting your search or check Dashboard Tools Settings to restore hidden tools.</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => setSearchQuery('')}
          >
            Clear Search
          </Button>
        </div>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => {
          setShowUpgradeModal(false);
          setSelectedTool(null);
        }}
        toolName={selectedTool?.name}
        toolDescription={`Unlock ${selectedTool?.name} and all other premium tools with a subscription.`}
      />
    </div>
  );
};

export default QuickActionsGrid;
